import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToHash } from "@/lib/scroll";
import { EASE } from "../site/motion";

const NODES = [
  { x: 70, y: 80, label: "api" },
  { x: 210, y: 56, label: "auth", pulse: true },
  { x: 352, y: 104, label: "ml" },
  { x: 118, y: 196, label: "db" },
  { x: 264, y: 182, pulse: true },
  { x: 404, y: 222, label: "cdn" },
  { x: 88, y: 300 },
  { x: 226, y: 284, label: "queue" },
  { x: 366, y: 316, pulse: true },
];

const LINKS = [
  [0, 1], [1, 2], [0, 3], [1, 4], [2, 5], [3, 4],
  [4, 5], [3, 6], [4, 7], [5, 8], [6, 7], [7, 8],
];

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SystemBoard() {
  return (
    <div className="relative">
      <div className="absolute -inset-10 rounded-[40px] bg-gradient-to-br from-blue-600/15 via-transparent to-violet-600/20 blur-2xl" />
      <div className="absolute inset-0 translate-x-5 translate-y-6 rounded-[26px] border border-slate-200/80 bg-slate-50/80" />
      <div className="relative overflow-hidden rounded-[26px] border border-slate-200 bg-white/85 shadow-[0_50px_90px_-45px_rgba(11,18,32,0.4)] backdrop-blur">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3.5">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
            bytevora.system — online
          </span>
          <span className="relative ml-auto flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-600" />
          </span>
        </div>
        <svg
          viewBox="0 0 480 360"
          className="block h-auto w-full"
          role="img"
          aria-label="Abstract system architecture diagram"
        >
          <defs>
            <pattern id="bv-dots" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="#E2E8F0" />
            </pattern>
            <linearGradient id="bv-line" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#2563EB" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
          </defs>
          <rect width="480" height="360" fill="url(#bv-dots)" opacity="0.55" />
          {LINKS.map(([a, b], i) => (
            <line
              key={i}
              x1={NODES[a].x}
              y1={NODES[a].y}
              x2={NODES[b].x}
              y2={NODES[b].y}
              stroke="url(#bv-line)"
              strokeWidth="1.4"
              opacity="0.5"
              className={i % 3 === 0 ? "dash-flow" : ""}
            />
          ))}
          {NODES.map((n, i) => (
            <g key={i}>
              {n.pulse && (
                <circle
                  cx={n.x}
                  cy={n.y}
                  r="5"
                  fill="none"
                  stroke="url(#bv-line)"
                  strokeWidth="1.5"
                  className="node-pulse"
                />
              )}
              <circle cx={n.x} cy={n.y} r="5.5" fill="#fff" stroke="url(#bv-line)" strokeWidth="2" />
              {n.label && (
                <text
                  x={n.x + 12}
                  y={n.y + 4}
                  fontSize="10"
                  fill="#94A3B8"
                  fontFamily="'Geist Mono', monospace"
                  letterSpacing="1.5"
                >
                  {n.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const boardX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const boardY = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const chipX = useTransform(sx, [-0.5, 0.5], [24, -24]);
  const chipY = useTransform(sy, [-0.5, 0.5], [16, -16]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const chips = [
    { label: "SAAS", cls: "-left-4 top-14 lg:-left-9", delay: "0s" },
    { label: "AI", cls: "-right-3 top-32 lg:-right-8", delay: "1.1s" },
    { label: "CLOUD", cls: "-left-3 bottom-24 lg:-left-10", delay: "0.6s" },
    { label: "API", cls: "right-6 -bottom-5", delay: "1.7s" },
  ];

  return (
    <section
      ref={ref}
      onMouseMove={handleMouse}
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-[72px]"
    >
      <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
      <div className="absolute -left-44 top-[-160px] h-[480px] w-[480px] rounded-full bg-blue-600/[0.13] blur-[120px]" aria-hidden="true" />
      <div className="absolute -right-44 top-1/3 h-[440px] w-[440px] rounded-full bg-violet-600/[0.14] blur-[120px]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-b from-transparent to-white" aria-hidden="true" />

      <div className="relative mx-auto grid min-h-[calc(100vh-72px)] w-full max-w-[1200px] items-center gap-16 px-6 pb-24 pt-14 md:px-8 lg:grid-cols-12 lg:pb-28 lg:pt-10">
        <motion.div style={{ y: contentY, opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          >
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.26em] text-slate-500 backdrop-blur"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600" />
              Software · Cloud · AI · Digital Products
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-8 text-[clamp(2.9rem,7vw,5.4rem)] font-extrabold leading-[0.99] tracking-[-0.035em] text-slate-900"
          >
            <MaskedLine delay={0.24}>Building technology</MaskedLine>
            <MaskedLine delay={0.36}>
              for{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                what&rsquo;s next.
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.58 }}
            className="mt-7 max-w-[540px] text-base leading-relaxed text-slate-600 md:text-lg"
          >
            Bytevora designs and engineers modern software, digital products, cloud platforms,
            and intelligent technology solutions that help businesses build, scale, and innovate.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-start-project-button"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              type="button"
              data-testid="hero-explore-services-button"
              onClick={() => scrollToHash("#services")}
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
            >
              Explore Services
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="mt-12 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            Accepting new projects — Lucknow, India
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: visualY }}
          className="relative hidden lg:col-span-5 lg:block"
        >
          <motion.div
            initial={{ opacity: 0, y: 48, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
          >
            <motion.div style={{ x: boardX, y: boardY }}>
              <SystemBoard />
            </motion.div>
            <motion.div style={{ x: chipX, y: chipY }} className="pointer-events-none absolute inset-0">
              {chips.map((c) => (
                <span
                  key={c.label}
                  style={{ animationDelay: c.delay }}
                  className={`floaty absolute ${c.cls} rounded-full border border-slate-200 bg-white px-3.5 py-1.5 font-mono text-[10px] tracking-[0.2em] text-slate-600 shadow-[0_14px_30px_-14px_rgba(11,18,32,0.35)]`}
                >
                  {c.label}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex" aria-hidden="true">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Scroll</span>
        <span className="scroll-cue block h-10 w-px bg-gradient-to-b from-blue-600 to-violet-600" />
      </div>
    </section>
  );
}
